import { writable, get } from 'svelte/store';

export type OfflineDownloadStatus =
  | { phase: 'idle' }
  | { phase: 'downloading'; current: number; total: number }
  | { phase: 'complete' }
  | { phase: 'error'; message: string };

const OFFLINE_KEY = `${__STORAGE_KEY_PREFIX__}-offline-complete`;

// Restore completed state if it was recorded for the current build version.
// Because __BUILD_VERSION__ changes on every new build, a stored value from an
// older build will never match and the button resets to idle automatically.
function initialStatus(): OfflineDownloadStatus {
  try {
    if (localStorage.getItem(OFFLINE_KEY) === __BUILD_VERSION__) {
      return { phase: 'complete' };
    }
  } catch { /* localStorage unavailable */ }
  return { phase: 'idle' };
}

export const offlineStatus = writable<OfflineDownloadStatus>(initialStatus());

// Extract all <img src> values from an HTML string.
function extractImageUrls(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll('img[src]'))
    .map(img => img.getAttribute('src')!)
    .filter(Boolean);
}

// Fetch a URL in the background so the service worker caches the response.
// Cross-origin images are fetched with no-cors so the SW can store the opaque
// response (requires CacheableResponsePlugin({statuses:[0,200]}) in vite.config).
async function cacheFetch(url: string): Promise<void> {
  const isExternal = url.startsWith('http://') || url.startsWith('https://');
  await fetch(url, isExternal ? { mode: 'no-cors' } : undefined);
}

export async function downloadAllContent(): Promise<void> {
  const current = get(offlineStatus);
  if (current.phase === 'downloading' || current.phase === 'complete') return;

  offlineStatus.set({ phase: 'downloading', current: 0, total: 0 });

  try {
    // 1. Fetch manifest to discover all content paths.
    const manifestRes = await fetch('/manifest.json');
    if (!manifestRes.ok) throw new Error('Failed to fetch manifest');
    const manifest = await manifestRes.json();

    const volumes: { path: string }[] = manifest.volumes ?? [];
    const wiki: { path: string }[] = manifest.wiki ?? [];

    // Build article and preview URLs.
    // volumes[].path has no prefix (e.g. "00-guide/01-overview")
    // wiki[].path has no prefix (e.g. "meta-test")
    const articleUrls: string[] = [
      ...volumes.map(v => `/articles/volumes/${v.path}.json`),
      ...wiki.map(w => `/articles/wiki/${w.path}.json`),
    ];
    const previewUrls: string[] = [
      ...volumes.map(v => `/previews/volumes/${v.path}.json`),
      ...wiki.map(w => `/previews/wiki/${w.path}.json`),
    ];

    const total = articleUrls.length + previewUrls.length;
    offlineStatus.set({ phase: 'downloading', current: 0, total });

    // 2. Fetch articles in batches — collect image URLs along the way.
    const BATCH = 6;
    let done = 0;
    const imageUrls = new Set<string>();

    for (let i = 0; i < articleUrls.length; i += BATCH) {
      await Promise.all(
        articleUrls.slice(i, i + BATCH).map(async (url) => {
          try {
            const res = await fetch(url);
            if (res.ok) {
              const data = await res.json();
              if (data.html) {
                for (const src of extractImageUrls(data.html)) {
                  imageUrls.add(src);
                }
              }
            }
          } catch { /* skip unreachable articles */ }
          offlineStatus.set({ phase: 'downloading', current: ++done, total });
        }),
      );
    }

    // 3. Fetch previews in batches.
    for (let i = 0; i < previewUrls.length; i += BATCH) {
      await Promise.all(
        previewUrls.slice(i, i + BATCH).map(async (url) => {
          try { await fetch(url); } catch { /* skip */ }
          offlineStatus.set({ phase: 'downloading', current: ++done, total });
        }),
      );
    }

    // 4. Cache images (local + external hotlinks). Errors are silently ignored
    //    since image availability is best-effort.
    const imageArray = Array.from(imageUrls);
    for (let i = 0; i < imageArray.length; i += BATCH) {
      await Promise.all(
        imageArray.slice(i, i + BATCH).map(url => cacheFetch(url).catch(() => {})),
      );
    }

    // 5. Persist completion state keyed to this build version.
    try { localStorage.setItem(OFFLINE_KEY, __BUILD_VERSION__); } catch { /* ignore */ }
    offlineStatus.set({ phase: 'complete' });

  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    offlineStatus.set({ phase: 'error', message });
  }
}
